import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Skeleton,
  Alert,
  Link,
  IconButton,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as WebsiteIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Star as YelpIcon,
  Google as GoogleIcon,
} from '@mui/icons-material';
import { portalApi, ContactInfo as ContactInfoType } from '../services/portalApi';
import { useTranslation } from 'react-i18next';

const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const Contact: React.FC = () => {
  const { t } = useTranslation();
  const [contactInfo, setContactInfo] = useState<ContactInfoType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await portalApi.getContactInfo();
        setContactInfo(data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatTime = (time: string | null) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>{t('Contact Us')}</Typography>
        <Card>
          <CardContent>
            <Skeleton width="50%" height={32} />
            <Skeleton width="80%" height={24} />
            <Skeleton width="60%" height={24} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (error || !contactInfo) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>{t('Contact Us')}</Typography>
        <Alert severity="error">{error || t('Unable to load contact information')}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('Contact Us')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('Get in touch with {{businessName}}', { businessName: contactInfo.businessName })}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
        }}
      >
        {/* Contact Info */}
        <Box>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                {contactInfo.logoUrl && (
                  <Box
                    component="img"
                    src={contactInfo.logoUrl}
                    alt={contactInfo.businessName}
                    sx={{ height: 80, maxWidth: 280, objectFit: 'contain', mr: 2 }}
                  />
                )}
                <Typography variant="h5">{contactInfo.businessName}</Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {contactInfo.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PhoneIcon sx={{ color: 'primary.main' }} />
                    <Link href={`tel:${contactInfo.phone}`} underline="hover">
                      {contactInfo.phone}
                    </Link>
                  </Box>
                )}

                {contactInfo.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <EmailIcon sx={{ color: 'primary.main' }} />
                    <Link href={`mailto:${contactInfo.email}`} underline="hover">
                      {contactInfo.email}
                    </Link>
                  </Box>
                )}

                {contactInfo.website && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <WebsiteIcon sx={{ color: 'primary.main' }} />
                    <Link
                      href={contactInfo.website.startsWith('http') ? contactInfo.website : `https://${contactInfo.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                    >
                      {contactInfo.website}
                    </Link>
                  </Box>
                )}
              </Box>

              {/* Social Links */}
              {(contactInfo.socialLinks.facebook ||
                contactInfo.socialLinks.instagram ||
                contactInfo.socialLinks.yelp ||
                contactInfo.socialLinks.google) && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('Follow Us')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {contactInfo.socialLinks.facebook && (
                      <IconButton
                        component="a"
                        href={contactInfo.socialLinks.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: '#1877F2' }}
                      >
                        <FacebookIcon />
                      </IconButton>
                    )}
                    {contactInfo.socialLinks.instagram && (
                      <IconButton
                        component="a"
                        href={contactInfo.socialLinks.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: '#E4405F' }}
                      >
                        <InstagramIcon />
                      </IconButton>
                    )}
                    {contactInfo.socialLinks.yelp && (
                      <IconButton
                        component="a"
                        href={contactInfo.socialLinks.yelp}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: '#D32323' }}
                      >
                        <YelpIcon />
                      </IconButton>
                    )}
                    {contactInfo.socialLinks.google && (
                      <IconButton
                        component="a"
                        href={contactInfo.socialLinks.google}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: '#4285F4' }}
                      >
                        <GoogleIcon />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Business Hours */}
        <Box>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('Business Hours')}
              </Typography>

              {contactInfo.businessHours.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {contactInfo.businessHours.map((hours) => (
                    <Box
                      key={hours.dayOfWeek}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        py: 0.5,
                        borderBottom: hours.dayOfWeek < 6 ? 1 : 0,
                        borderColor: 'divider',
                      }}
                    >
                      <Typography sx={{ fontWeight: 500 }}>
                        {t(dayNames[hours.dayOfWeek])}
                      </Typography>
                      <Typography color={hours.isOpen ? 'text.primary' : 'text.secondary'}>
                        {hours.isOpen
                          ? `${formatTime(hours.openTime)} - ${formatTime(hours.closeTime)}`
                          : t('Closed')}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography color="text.secondary">
                  {t('Contact us for business hours')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};
